<script setup lang="ts">
import { useResultStore } from '~/store/result'
import { useDetailResultStore } from '~/store/detail_result'
import { ID_KENO_FR, ID_EURO_MILLIONS, ID_LOTTO_FACIL } from '~/utils/constants/idsLotteries'
import { formatCurrency } from '~/utils/formatCurrency'
import IconStar from '~/components/icons/IconStar.vue'
import { useAuthStore } from '~/store/auth'
import { storeToRefs } from 'pinia'
import BoxNumbers from '~/components/pages/results/BoxNumbers.vue'
import BackgroundBanner from '~/components/effects/BackgroundBanner.vue'
import Pagination from '~/components/pages/results/Pagination.vue'
import { IDS_LOTTERIES_CLASSIC } from '~/utils/constants/home.js'
import { toQueryString } from '~/utils/queryString'
import { filteredQueryParams } from '~/utils/filteredQueryParams'
import LoaderResults from '~/components/pages/results/LoaderResults.vue'
import CardDetailResult from '~/components/pages/results/tabs/results/CardDetailResultNew.vue'
import { ENABLE_ANALYZE_PERFORMANCE_HISTORY } from '~/config/featureFlags'

defineOptions({
	name: 'ModalAnalyzePerfomance',
})

interface Pagination {
	current: number
	per_page: number
	total_pages: number
}

interface Lottery {
	id: number
	name: string
	slug: string
	image: string
}

interface Params {
	lottery_id: null
	page_number: number
	regular_balls: number[]
	regular_ball_quantity: number | undefined
	extra_ball_quantity?: number | undefined
	extra_balls?: any
	number_of_balls?: number
}

interface NumbersBlock {
	first: (string | null)[]
	last: (string | null)[] | any
	prize: (string | null)[] | any
}

interface RowBlock {
	id: number
	prize_id: number
	numbers: NumbersBlock
	prize_group?: {
		extra: number
		regular: number
	}
	jackpot?: number
	type_currency?: string
	date?: string
}

interface QueryParam {
	lottery_id: number
	first_number: number
	second_number: number
	third_number: number
	fourth_number: number
	fifth_number: number
	page_number: number
	prize_id: number
	first_number_filter?: number
	second_number_filter?: number
	third_number_filter?: number
	fourth_number_filter?: number
	fifth_number_filter?: number
}

interface DetailResult {
	jackpot: number
	type_currency: string
	prizes_count: number
	[key: string]: any
}

const { $hasFeature: _$hasFeature } = useNuxtApp()
const $hasFeature = _$hasFeature ?? (() => false)

const resultStore = useResultStore()
const detailResultStore = useDetailResultStore()
const authStore = useAuthStore()
const { loggedIn } = storeToRefs(authStore)
const { t: $t, locale } = useI18n()
const route = useRoute()
const router = useRouter()

const initialBlock = {
	first: [],
	last: [],
	prize: null,
}

const initialText = {
	title: 'Analyzing History',
	description: 'Searching through all recorded years',
}

const loadingText = ref({
	title: $t(initialText.title),
	description: `${$t(initialText.description)}...`,
})
let timeoutId: ReturnType<typeof setTimeout> | null = null

const getLottery = computed<Lottery | null>(() => {
	if (resultStore.lottery_id === null) return null
	return fetchLotterySelect(resultStore.lottery_id)
})

const getLotteries = computed<RowBlock[]>(() => {
	if (detailResultStore.lotteries === null) return []
	return detailResultStore.lotteries
})

const prizesCount = computed(() => {
	const detail = detailResultStore.detail as { prizes_count?: number } | null
	return detail?.prizes_count ?? 0
})

const detailPrizesCount = computed(() => {
	const detail = detailResultStore.detail as { prizes_count?: number } | null
	return detail?.prizes_count ?? 0
})

const frecuencyTextHtml = computed(() => {
	return $t('Your numbers have hit this tier {frequency} historically.', {
		frequency: `<span class="font-semibold text-primary-500">${prizesCount.value} ${$t('times')}</span>`,
	})
})

const unlockTextHtml = computed(() => {
	return $t('See exactly {highlighted_text} your numbers won and analyze the frequency patterns.', {
		highlighted_text: `<span class="font-medium text-primary-500">${$t('when')}</span>`,
	})
})

const getClassFirstBlock = computed(() => {
	if (detailResultStore?.lottery_id === ID_LOTTO_FACIL)
		return 'flex flex-wrap justify-center sm:justify-between items-center gap-y-5 sm:gap-y-3 gap-x-2.5'
	return 'flex flex-wrap gap-y-3 gap-x-1 xl:gap-x-1.5'
})

const getAccess = computed(() => {
	if ($hasFeature(ENABLE_ANALYZE_PERFORMANCE_HISTORY) === false) return loggedIn.value
	return true
})

const pagination = computed<Pagination | null>(() => {
	return detailResultStore.pagination
})

const get1 = (v: unknown) => (Array.isArray(v) ? v[0] : v)

const isDetailModal = computed(() => get1(route.query.modal) === 'detail')

const modalId = computed(() => {
	const s = get1(route.query.modal_id)
	return s ? Number(s) : null
})

const lotteriesLength = computed<number>(() => {
	const lotteries = resultStore.lotteries as RowBlock[] | null | undefined
	return Array.isArray(lotteries) ? lotteries.length : 0
})

const close = () => {
	resultStore.SET_STATE_RESULT({
		type: 'showModalDetail',
		value: false,
	})
	onClose()
}

const closeModal = () => {
	const q = { ...route.query }
	delete q.modal
	delete q.modal_id

	router.replace({ query: q })
}

const onClose = () => {
	detailResultStore.SET_STATE_DETAIL_RESULT({
		type: 'current_page',
		value: 1,
	})
	if (loggedIn.value === false) return
	closeModal()
}

const handleUnlock = () => {
	close()
	resultStore.SET_STATE_RESULT({
		type: 'showModalSignUp',
		value: true,
	})
}

const getQuery = (position: number) => {
	if (!resultStore.lotteries) return
	const row: RowBlock = resultStore.lotteries[position]
	const numbers: NumbersBlock = resultStore.numbers ?? initialBlock

	if (resultStore.lottery_id && IDS_LOTTERIES_CLASSIC.includes(resultStore.lottery_id)) {
		const [first_number, second_number, third_number, fourth_number, fifth_number] =
			numbers.first?.map((num: any) => Number(num)) ?? []

		const [first_number_filter, second_number_filter, third_number_filter, fourth_number_filter, fifth_number_filter] =
			row?.numbers.prize ?? []
		const query: QueryParam = {
			lottery_id: resultStore.lottery_id,
			first_number,
			second_number,
			third_number,
			fourth_number,
			fifth_number,
			page_number: detailResultStore.current_page,
			prize_id: row?.prize_id,
		}
		if (first_number_filter.number !== null) {
			query.first_number_filter = first_number_filter.number
		}
		if (second_number_filter.number !== null) {
			query.second_number_filter = second_number_filter.number
		}
		if (third_number_filter.number !== null) {
			query.third_number_filter = third_number_filter.number
		}
		if (fourth_number_filter.number !== null) {
			query.fourth_number_filter = fourth_number_filter.number
		}
		if (fifth_number_filter.number !== null) {
			query.fifth_number_filter = fifth_number_filter.number
		}

		return query
	}
	const params: Params = {
		lottery_id: resultStore.lottery_id,
		page_number: detailResultStore.current_page,
		regular_balls: numbers && numbers?.first ? numbers.first.map((num) => Number(num)) : [],
		regular_ball_quantity: row.prize_group?.regular,
	}
	if (row.prize_group?.extra && row.prize_group?.extra !== null) {
		params.extra_ball_quantity = row.prize_group?.extra
	}
	if (ID_KENO_FR === resultStore.lottery_id) {
		params.number_of_balls = numbers.first.length
	}
	if (row.numbers?.last?.length > 0) {
		params.extra_balls = numbers.last.map((num: string) => Number(num))
	}

	return params
}

const changePage = async (page: number) => {
	detailResultStore.SET_STATE_DETAIL_RESULT({
		type: 'current_page',
		value: page,
	})

	const params =
		detailResultStore.select_position === null
			? null
			: toQueryString(filteredQueryParams(getQuery(detailResultStore.select_position)))
	if (params === null) return
	if (resultStore.lottery_id && IDS_LOTTERIES_CLASSIC.includes(resultStore.lottery_id)) {
		await detailResultStore.fetchDetailResultSaturday(params, locale.value)
		return
	}

	await detailResultStore.fetchDetailResult(params, locale.value)
}

watch(
	() => detailResultStore.loading,
	(isLoading) => {
		if (isLoading) {
			loadingText.value = {
				title: $t(initialText.title),
				description: `${$t(initialText.description)}...`,
			}

			timeoutId = setTimeout(() => {
				if (detailResultStore.loading) {
					loadingText.value = {
						title: $t('Preparing Results'),
						description: `${$t('Organizing all the results')}...`,
					}
				}
			}, 2500)
		} else {
			if (timeoutId) {
				clearTimeout(timeoutId)
				timeoutId = null
			}
		}
	},
	{ immediate: true },
)

const openDetailModalFromQuery = () => {
	const isOpen = isDetailModal.value
	const id = modalId.value
	if (!isOpen || !id) return

	const list = (resultStore.lotteries || []) as RowBlock[]
	if (!list.length) return

	const position = list.findIndex((x) => Number(x.id) === id)
	if (position < 0) return

	detailResultStore.SET_STATE_DETAIL_RESULT({ type: 'select_position', value: position })
	detailResultStore.SET_STATE_DETAIL_RESULT({ type: 'detail', value: list[position] })
	resultStore.SET_STATE_RESULT({ type: 'showModalDetail', value: true })
}

watch(
	[isDetailModal, modalId, lotteriesLength],
	async () => {
		await nextTick()
		openDetailModalFromQuery()
	},
	{ immediate: true },
)
</script>

<template>
	<UModal
		v-model="resultStore.showModalDetail"
		:ui="{
			overlay: {
				background: 'bg-[#040404D4] bg-opacity-80',
			},
			width: 'w-full sm:max-w-2xl',
			container: 'items-center sm:items-start',
			padding: 'p-6 sm:p-8',
			rounded: 'rounded-2xl sm:rounded-xl',
			ring: 'ring-1 ring-[#1D283A]',
		}"
		@close="onClose"
	>
		<div
			class="relative z-10 rounded-2xl sm:rounded-xl"
			:class="getAccess ? 'bg-secondary-900' : 'bg-secondary-700'"
		>
			<BackgroundBanner class="sm:hidden" />
			<div class="flex justify-end">
				<UButton
					color="white"
					variant="link"
					size="sm"
					icon="i-heroicons-x-mark-20-solid"
					class="text-neutral-50 pt-5 pr-4"
					@click="close"
				/>
			</div>
			<div class="p-5 sm:p-0 space-y-4">
				<header class="text-neutral-50 space-y-2 sm:space-y-4 p-4 sm:p-6 text-center sm:text-left">
					<h2 class="text-lg sm:text-xl leading-8 font-semibold">{{ $t('Analyze perfomance') }}</h2>
					<p class="text-sm sm:text-base leading-6">
						{{ $t('See the date and amount won each time by this number combination') }}.
					</p>
				</header>
				<div class="sm:bg-[#10B77F]/5 sm:p-6 space-y-5">
					<div class="grid sm:grid-cols-2 gap-4 sm:gap-5">
						<div
							class="bg-secondary-900 sm:bg-secondary-700 rounded-[20px] sm:rounded-xl px-4 py-3 sm:py-4 flex sm:items-center gap-2 sm:gap-4"
						>
							<div
								class="sm:size-20 sm:flex items-center justify-center sm:bg-green-400/20"
								:class="getAccess ? 'rounded-2xl' : 'rounded-full'"
							>
								<UIcon
									name="i-hugeicons:coins-01"
									class="size-6 sm:size-10 text-primary-400"
								/>
							</div>
							<div class="flex-1 text-neutral-50 space-y-3">
								<h3 class="text-base sm:text-lg font-bold sm:font-semibold">
									{{ getLottery?.id === ID_EURO_MILLIONS ? $t(getLottery.name) : getLottery?.name }}
								</h3>

								<div
									class="space-y-0.5 flex flex-row sm:flex-col justify-between sm:justify-start items-center sm:items-start"
								>
									<p class="text-sm leading-5">{{ $t('Total Prize Amount') }}</p>
									<p
										v-if="detailResultStore.detail"
										class="text-sm sm:text-lg font-semibold"
									>
										{{
											formatCurrency(
												(detailResultStore.detail as DetailResult)?.jackpot,
												(detailResultStore.detail as DetailResult)?.type_currency,
											)
										}}
									</p>
								</div>
							</div>
						</div>
						<div
							class="bg-secondary-900 sm:bg-secondary-700 rounded-[20px] sm:rounded-xl px-4 py-3 sm:py-4 flex sm:items-center gap-2 sm:gap-4"
						>
							<div
								class="sm:size-20 sm:flex items-center justify-center sm:bg-green-400/20"
								:class="getAccess ? 'rounded-full' : 'rounded-2xl'"
							>
								<UIcon
									name="i-fluent:pulse-square-20-regular"
									class="size-6 sm:size-10 text-primary-400"
								/>
							</div>
							<div class="flex-1 text-neutral-50 space-y-1.5 sm:space-y-3">
								<h3 class="text-base sm:text-lg font-bold sm:font-semibold">
									{{ $t('Match Frequency') }}
								</h3>

								<div
									class="space-y-0.5 flex flex-row sm:flex-col justify-between sm:justify-start items-center sm:items-start"
								>
									<p class="text-sm leading-5">{{ $t('Times Matched') }}</p>
									<p
										v-if="detailResultStore.detail"
										class="text-sm sm:text-lg font-semibold"
									>
										{{ detailPrizesCount }}
									</p>
								</div>
							</div>
						</div>
					</div>

					<div
						v-if="detailResultStore.detail"
						class="hidden sm:flex items-center gap-2 p-4 rounded-xl border border-[#10B77F]/20 bg-[#10B77F]/5 text-sm"
					>
						<IconStar class="size-4 text-primary-500" />
						<p
							class="text-sm text-[#FCFBF8] leading-5"
							v-html="frecuencyTextHtml"
						></p>
					</div>
				</div>

				<section
					class="sm:px-6 pt-4 space-y-6 sm:space-y-4"
					:class="detailResultStore.loading ? 'pb-6' : 'pb-14'"
				>
					<div class="flex items-center gap-2">
						<UIcon
							name="i-lucide:calendar"
							class="size-4 text-[#7588A3]"
						/>
						<h3 class="flex items-center gap-2 text-base text-neutral-50 font-medium leading-7 sm:leading-6">
							{{ $t('Draw-by-Draw History') }}
						</h3>
					</div>
					<LoaderResults
						v-if="detailResultStore.loading"
						class-container="flex justify-center items-center py-5"
						:show-custom-message="true"
						:title="$t(loadingText.title)"
						:description="$t(loadingText.description)"
					/>
					<div
						v-else
						class="relative"
					>
						<div
							v-if="getAccess === false"
							class="absolute inset-0 flex sm:items-center justify-center z-10"
						>
							<div
								class="sm:mx-8 sm:bg-secondary-700 rounded-2xl w-full sm:max-w-sm px-2 sm:px-8 py-8 text-center shadow-lg space-y-4"
							>
								<div class="w-16 h-16 mx-auto rounded-full bg-[#18212F] flex items-center justify-center">
									<UIcon
										name="i-mono-icons:unlock"
										class="size-10 text-primary-400 scale-x-[-1]"
									/>
								</div>
								<div class="space-y-6">
									<div class="space-y-2">
										<h4 class="text-base sm:text-[18px] font-semibold text-neutral-50">
											{{ $t('Unlock Detailed History') }}
										</h4>
										<p
											class="text-sm text-[#7588A3]"
											v-html="unlockTextHtml"
										/>
									</div>
									<div class="space-y-4">
										<UButton
											color="primary"
											variant="solid"
											icon="i-mono-icons:unlock"
											:label="$t('Unlock Full Report')"
											class="text-[#080C16] rounded-full font-semibold sm:font-bold text-sm py-2"
											:class="{ 'px-8': locale === 'en' }"
											@click="handleUnlock"
										/>
										<p class="text-xs text-[#7588A3] leading-4">
											{{ $t('Free sign-up required to access historical data') }}.
										</p>
									</div>
								</div>
							</div>
						</div>

						<div
							class="space-y-6 sm:space-y-3"
							:class="{
								'blur-lg sm:blur-sm bg-[#0C1322] sm:bg-transparent border sm:border-none border-[#1d283a]/50 sm:min-h-80':
									getAccess === false,
							}"
						>
							<template
								v-for="(lottery, index) in getLotteries"
								:key="index"
							>
								<CardDetailResult
									:lottery="lottery"
									:show-btn-detail="false"
									class="sm:hidden"
								/>
								<div
									class="hidden sm:flex bg-[#18212F]/50 border border-[#1d283a] rounded-xl p-4 items-center justify-between"
								>
									<div class="flex items-center gap-4">
										<div class="text-sm text-[#7588A3] min-w-24">{{ lottery?.date }}</div>
										<div class="flex items-center gap-2">
											<BoxNumbers
												:numbers="lottery?.numbers"
												:show-btn-detail="false"
												class="w-fit mx-auto"
												:class="{ 'max-w-56': detailResultStore?.lottery_id === ID_LOTTO_FACIL }"
												:class-container-first-block="getClassFirstBlock"
											/>
										</div>
									</div>
									<div class="text-[18px] font-bold text-primary-500">
										{{ formatCurrency(lottery?.jackpot, lottery.type_currency) }}
									</div>
								</div>
							</template>
						</div>
						<div
							v-if="getAccess === true && pagination && pagination?.total_pages > 1"
							class="flex justify-center pt-6"
						>
							<Pagination
								:total="pagination?.total_pages"
								:page-count="pagination?.per_page"
								:current="pagination?.current"
								@change="changePage"
							/>
						</div>
					</div>
				</section>
			</div>
		</div>
	</UModal>
</template>
